import type { AcademyAuthority, SubscribeItem } from '@mathflat/domain/Academy'
import { makeAutoObservable } from 'mobx'

import { FeaturePermission } from './FeaturePermission'
import { SubscriptionPermission } from './SubscriptionPermission'

// 학생앱에서 사용하는 사용자 역할 타입
type UserRole = 'PARENT' | 'STUDENT'

/**
 * Permission facade.
 *
 * 2개의 하위 Permission을 합성하여 단일 인터페이스로 노출:
 * - .bySubscription → SubscriptionPermission (페이지 내 기능 + SOLO 검증)
 * - .byFeature      → FeaturePermission (BE restrictions 블랙리스트)
 *
 * 학부모/학생 역할 분기는 .isParent boolean 단일 게터로 제공.
 * 라우트 진입 결정은 ROUTE_PERMISSION_MAP + restrictions 기반으로 .byFeature.isRouteAllowed() 사용.
 *
 * MobX makeAutoObservable을 사용해 반응형으로 동작한다.
 * updateRestrictions 호출 시 byFeature가 새 인스턴스로 교체되어 UI가 자동 반응한다.
 *
 * **네이밍 컨벤션** (boolean 게터/변수):
 * - 도메인/모델(Permission 클래스 안) — "허용되었나" 게이팅 판정: `is~Allowed`
 *   예) `byFeature.isAllowed('DAILY_WORKSHEET')`, `byFeature.isRouteAllowed(pathname)`,
 *       `UserPermission.isDailyWorksheetAllowed`
 * - UI 호출처에서 `byFeature.isAllowed(...)` 결과로 만든 로컬 변수: `is~Enabled`
 *   예) `const isExamWriteEnabled = byFeature.isAllowed(EXAM_WRITE_KEY)`
 *   모델 게터를 그대로 destructure 할 때는 게터 이름(`isXxxAllowed`)을 유지한다 — 별칭 부여 X.
 * - UI 가시성(visibility only) — 권한 무관, 스크롤·드로어 등 일시 상태: `is~Visible`
 *
 * 단순한 상태/타입 boolean(`isParent`, `isPremium`, `isBasic`, `isSolo`)은 컨벤션 적용 대상 아님.
 */
export class UserPermission {
  // 페이지 내 기능 플래그 + SOLO 구독 검증 + 플랜 분기 SSOT
  private _subscriptionPermission: SubscriptionPermission

  // BE restrictions 블랙리스트 기반 기능 게이팅
  private _featurePermission: FeaturePermission

  // 역할 편의 getter 용으로 저장
  private readonly _userRole: UserRole

  constructor(params: {
    userRole: UserRole
    academyAuthority: AcademyAuthority
    subscribeItems: SubscribeItem[] | null
    isSchoolflat: boolean
    restrictions?: string[]
  }) {
    this._userRole = params.userRole

    // 구독 기반 기능 플래그 초기화
    this._subscriptionPermission = new SubscriptionPermission({
      academyAuthority: params.academyAuthority,
      subscribeItems: params.subscribeItems,
    })

    // BE restrictions 블랙리스트 초기화 (없으면 빈 배열 = 전체 허용)
    this._featurePermission = new FeaturePermission(params.restrictions ?? [])

    // MobX 반응형 등록 — updateRestrictions 호출 시 byFeature 교체가 UI에 자동 반영됨
    makeAutoObservable(this)
  }

  /** 학부모 역할 여부 */
  get isParent(): boolean {
    return this._userRole === 'PARENT'
  }

  // --- 하위 Permission 접근자 ---

  /**
   * 페이지 내 기능 플래그 + SOLO 구독 검증 반환
   *
   * @example
   * if (permission.bySubscription.isSubscribed({ schoolType: 'MIDDLE', grade: 2 })) { ... }
   */
  get bySubscription() {
    return this._subscriptionPermission
  }

  /**
   * BE restrictions 기반 기능 게이팅 반환 (블랙리스트 방식)
   *
   * @example
   * if (permission.byFeature.isAllowed('CHALLENGE_WORKSHEET')) { ... } // 기능 권한
   * if (permission.byFeature.isRouteAllowed(pathname)) { ... }          // 라우트 권한
   */
  get byFeature() {
    return this._featurePermission
  }

  /**
   * BE restrictions 업데이트 (토큰 재발급 시 호출)
   * MobX observable이므로 교체 시 byFeature에 의존하는 UI가 자동 재렌더링된다.
   */
  updateRestrictions(restrictions: string[]) {
    // 새 FeaturePermission 인스턴스로 교체 — MobX가 변경을 감지함
    this._featurePermission = new FeaturePermission(restrictions)
  }

  // --- 플랜 타입 편의 getter (SubscriptionPermission 위임) ---

  /** PREMIUM 플랜 여부 */
  get isPremium(): boolean {
    return this._subscriptionPermission.isPremium
  }

  /** BASIC 플랜 여부 */
  get isBasic(): boolean {
    return this._subscriptionPermission.isBasic
  }

  /** SOLO 플랜 여부 */
  get isSolo(): boolean {
    return this._subscriptionPermission.isSolo
  }

  get isDailyWorksheetAllowed(): boolean {
    return this._featurePermission.isAllowed('DAILY_WORKSHEET')
  }
}
